import { useState } from "react";
import { Loader2, Send, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  askJurisIAv2,
  getApiErrorDetail,
  type RechercheResponseV2,
  type ResponseMode,
} from "@/lib/api";
import { classifyApiError } from "@/hooks/useApiErrorState";
import { ModeSelector } from "./ModeSelector";
import { SyllogismDisplay } from "./SyllogismDisplay";
import { ClaimsList } from "./ClaimsList";
import { MetaStrip } from "./MetaStrip";
import { CoverageBadge } from "./CoverageBadge";
import { RefusalBanner } from "./RefusalBanner";
import { GenerationProgress } from "./GenerationProgress";

interface AIAssistantV2Props {
  caseFileId?: number;
  /** Question initiale pré-remplie (utile depuis CaseDetail). */
  initialQuestion?: string;
  /** Mode initial — défaut quick. */
  initialMode?: ResponseMode;
}

/**
 * Assistant IA v2 — le « poste de travail juridique » du CdC #9.
 *
 * Compose les 5 sous-composants (ModeSelector, SyllogismDisplay,
 * ClaimsList, MetaStrip, RefusalBanner) en une expérience intégrée.
 * Aucune logique métier ici : tout vit dans le moteur core (Lots 2-3-6),
 * ce composant n'est qu'une vue.
 *
 * **Coexiste avec v1** : utilisé conditionnellement par `Recherche.tsx`
 * en fonction de `featureFlags.isIaV2Enabled()`.
 */
export function AIAssistantV2({
  caseFileId,
  initialQuestion,
  initialMode = "quick",
}: AIAssistantV2Props) {
  const [question, setQuestion] = useState(initialQuestion ?? "");
  const [mode, setMode] = useState<ResponseMode>(initialMode);
  const [submitting, setSubmitting] = useState(false);
  const [response, setResponse] = useState<RechercheResponseV2 | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isArt52, setIsArt52] = useState(false);

  const onSubmit = async () => {
    const q = question.trim();
    if (q.length < 3 || submitting) return;
    setSubmitting(true);
    setError(null);
    setIsArt52(false);
    try {
      const res = await askJurisIAv2(q, { caseFileId, mode });
      setResponse(res);
    } catch (err) {
      const c = classifyApiError(err);
      if (c.kind === "art52") {
        setIsArt52(true);
        setError(c.detail);
      } else if (c.kind !== "none") {
        setError(c.detail);
      } else {
        setError(
          getApiErrorDetail(err) ??
            "Service IA v2 indisponible. La feature flag IA_V2_ENABLED doit être activée côté backend.",
        );
      }
      setResponse(null);
    } finally {
      setSubmitting(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      void onSubmit();
    }
  };

  return (
    <div className="space-y-4">
      {/* Composer */}
      <div className="gsf-surface p-5 space-y-4">
        <ModeSelector value={mode} onChange={setMode} disabled={submitting} />
        <Textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Ex : conditions d'application de l'article 13 AUDA ?"
          rows={4}
          disabled={submitting}
          className="resize-none"
        />
        <div className="flex justify-between items-center gap-3">
          <div
            className="flex items-center gap-1 text-[11px]"
            style={{ color: "var(--arbitre-slate-500)" }}
          >
            <Sparkles size={11} />
            <span>v2 · syllogisme + claims + critique · ⌘+Enter</span>
          </div>
          <Button
            onClick={() => void onSubmit()}
            disabled={submitting || question.trim().length < 3}
            style={{
              background: "var(--ai-violet)",
              color: "var(--arbitre-slate-100)",
            }}
          >
            {submitting ? (
              <>
                <Loader2 size={14} className="animate-spin mr-2" />
                Génération v2…
              </>
            ) : (
              <>
                <Send size={14} className="mr-2" />
                Soumettre
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Bandeau de progression (10-30s typique pour Mistral). Affiche
          un compteur + des etapes graduees pour eviter la perception
          de bug pendant l'attente. Cf. AI_GENERATION_TIMEOUT_MS = 90s. */}
      {submitting && <GenerationProgress variant="recherche-v2" />}

      {/* Erreur */}
      {error && (
        <div
          role="alert"
          className="rounded-md border p-4 text-sm"
          style={{
            borderColor: isArt52
              ? "color-mix(in oklch, var(--alert-orange) 35%, transparent)"
              : "color-mix(in oklch, var(--block-red) 40%, transparent)",
            background: isArt52
              ? "color-mix(in oklch, var(--alert-orange) 12%, transparent)"
              : "color-mix(in oklch, var(--block-red) 10%, transparent)",
            color: isArt52 ? "var(--alert-orange)" : "rgb(254 202 202)",
          }}
        >
          {isArt52 && (
            <strong className="block mb-1">
              Art. 52 AI Act — profil non vérifié
            </strong>
          )}
          {error}
        </div>
      )}

      {/* Réponse structurée */}
      {response && (
        <div className="space-y-3">
          {response.refusal && <RefusalBanner refusal={response.refusal} />}
          <SyllogismDisplay syllogism={response.syllogism} />
          <MetaStrip
            classification={response.classification}
            confidence={response.confidence}
            needsHumanReview={response.needs_human_review}
            model={response.model}
            simulated={response.simulated}
          />
          {response.coverage && <CoverageBadge coverage={response.coverage} />}
          {/* Mode legal_note : on conserve l'answer Markdown brut pour
              donner accès à la version humaine lisible. */}
          {(response.mode === "legal_note" ||
            response.mode === "counter_argument") && (
            <div
              className="gsf-surface p-4 text-sm whitespace-pre-wrap"
              style={{
                color: "var(--arbitre-slate-100)",
                lineHeight: 1.6,
              }}
            >
              {response.answer}
            </div>
          )}
          <ClaimsList claims={response.claims} sources={response.sources_used} />
        </div>
      )}
    </div>
  );
}
